import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, unwrap, type Schemas } from '@/api/client';
import { qk } from '@/api/queries';
import type { Organization, User } from '@/api/types';
import { useSession } from '@/app/session';
import { Avatar, Button, Card, IconButton, Modal, ModalFooter, ModalHeader, Pill, Select, useModalTitleId, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { putMembers } from './api';
import { useOrg } from './OrganizationPage';
import s from './organizations.module.css';

type Row = { id: string; name: string; role: 'owner_pm' | 'pm' | 'admin'; mfa?: boolean; extra: string[] };

/** The scopes an Admin may grant to a PM (apps/api/app/core/scopes.py GRANTABLE). */
const GRANTABLE = ['deployment:publish_production', 'subscription:override_price'];

const staffQuery = {
  queryKey: [...qk.users, 'all'],
  queryFn: () => unwrap(api.GET('/admin/users', { params: { query: { size: 100, is_active: true } } })),
  select: (p: { items: User[] }) => p.items,
};

/** Пользователи (DESIGN_SPEC §4.3): staff with access — the organization's PMs and the Admins. */
export function UsersTab() {
  const { org } = useOrg();
  const { t } = useTranslation('organizations');
  const { isAdmin, can } = useSession();
  const [managing, setManaging] = useState(false);
  // Admins see every organization, so they are staff with access too; listing them is Admin-only.
  const staff = useQuery({ ...staffQuery, enabled: isAdmin }).data;
  const byId = new Map((staff ?? []).map((u) => [u.id, u]));
  const rows: Row[] = [
    ...[...org.members]
      .sort((a, b) => (a.member_role === 'owner_pm' ? -1 : b.member_role === 'owner_pm' ? 1 : 0))
      .map((m) => {
        const p = m.user;
        // The granted scopes are on the Admin's user list only (a PM's view of the team has none).
        const extra = (byId.get(p.id)?.extra_scopes ?? []).filter((x) => GRANTABLE.includes(x));
        return { id: p.id, name: p.full_name, role: m.member_role, mfa: byId.get(p.id)?.mfa_enabled ?? p.mfa_enabled, extra };
      }),
    ...(staff ?? []).filter((u) => u.role === 'admin' && u.is_active).map((u) => ({ id: u.id, name: u.full_name, role: 'admin' as const, mfa: u.mfa_enabled, extra: [] })),
  ];
  return (
    <>
      <Card padding={8} className={s.users}>
        <div className={s.usersHead}>
          <span className={s.cardTitle}>{t('users.title')}</span>
          <span className={s.usersNote}>{t('users.note')}</span>
        </div>
        {rows.map((r) => (
          <div key={`${r.role}-${r.id}`} className={s.userRow}>
            <Avatar name={r.name} size={38} />
            <div className={s.userText}>
              <span className={s.userName}>{r.name}</span>
              <span className={s.userRole}>{t(`users.role.${r.role}`)}</span>
            </div>
            {/* P6-D02 (additive, C10): the PM's extra permissions, granted by an Admin. */}
            {r.extra.map((x) => (
              <Pill key={x} tone="acc" size="tagMd" title={t('users.extra_title')}>
                {(t as unknown as (k: string) => string)(`users.extra.${x.replace(':', '__')}`)}
              </Pill>
            ))}
            {r.mfa !== undefined && (
              <Pill tone={r.mfa ? 'ok' : 'warn'} size="tagMd">
                {r.mfa ? t('users.mfa_on') : t('users.mfa_off')}
              </Pill>
            )}
          </div>
        ))}
      </Card>
      {/* Assigning PMs is additive (P1-F02) and Admin-only (member:write). */}
      {can('member:write') && (
        <Button variant="secondary" size={38} icon="users" iconSize={15} className={s.alignStart} onClick={() => setManaging(true)}>
          {t('users.manage')}
        </Button>
      )}
      {managing && <ManageMembers org={org} pms={(staff ?? []).filter((u) => u.role === 'pm' && u.is_active)} onClose={() => setManaging(false)} />}
    </>
  );
}

/** The team editor: add a PM, make one responsible (owner_pm), remove. PUT /admin/organizations/{id}/members. */
function ManageMembers({ org, pms, onClose }: { org: Organization; pms: User[]; onClose: () => void }) {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const toast = useToast();
  const qc = useQueryClient();
  const [members, setMembers] = useState<Schemas['MemberIn'][]>(org.members.map((m) => ({ user_id: m.user.id, member_role: m.member_role })));
  const [pick, setPick] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const name = (id: string) => pms.find((u) => u.id === id)?.full_name ?? org.members.find((m) => m.user.id === id)?.user.full_name ?? id;
  const free = pms.filter((u) => !members.some((m) => m.user_id === u.id));
  const chosen = pick || free[0]?.id || '';

  const save = useMutation({
    mutationFn: () => putMembers(org.id, members),
    onSuccess: (next) => {
      qc.setQueryData(qk.organization(org.id), next);
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      toast(t('users.saved'));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const submit = () => {
    if (members.length && !members.some((m) => m.member_role === 'owner_pm')) return setProblem(t('users.owner_required'));
    save.mutate();
  };

  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('users.manage_title')} sub={t('users.manage_sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <div className={s.team}>
        {members.map((m) => (
          <div key={m.user_id} className={s.teamRow}>
            <Avatar name={name(m.user_id)} size={34} />
            <div className={s.userText}>
              <span className={s.userName}>{name(m.user_id)}</span>
              <span className={s.userRole}>{t(`users.role.${m.member_role ?? 'pm'}`)}</span>
            </div>
            {m.member_role !== 'owner_pm' && (
              <IconButton
                icon="star"
                label={t('users.make_owner')}
                tone="outline"
                size={32}
                iconSize={14}
                onClick={() => setMembers((xs) => xs.map((x) => ({ ...x, member_role: x.user_id === m.user_id ? 'owner_pm' : 'pm' })))}
              />
            )}
            <IconButton icon="trash-2" label={t('users.remove')} tone="outline" size={32} iconSize={14} onClick={() => setMembers((xs) => xs.filter((x) => x.user_id !== m.user_id))} />
          </div>
        ))}
        {free.length ? (
          <div className={s.teamAdd}>
            <Select aria-label={t('users.add_label')} value={chosen} onChange={(e) => setPick(e.target.value)} options={free.map((u) => ({ value: u.id, label: u.full_name }))} />
            <Button
              variant="secondary"
              size={46}
              icon="plus"
              onClick={() => {
                setMembers((xs) => [...xs, { user_id: chosen, member_role: xs.some((x) => x.member_role === 'owner_pm') ? 'pm' : 'owner_pm' }]);
                setPick('');
              }}
            >
              {t('users.add')}
            </Button>
          </div>
        ) : (
          <span className={s.emptyNote}>{t('users.no_pms')}</span>
        )}
      </div>
      {problem && (
        <span className={s.problem} role="alert">
          {problem}
        </span>
      )}
      <ModalFooter>
        <Button size={46} variant="secondary" onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} onClick={submit} disabled={save.isPending}>
          {t('edit.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
